import { Storage } from '@ionic/storage';
import { Router, ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { BasePage } from './../base.page';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { WardrobeService } from './../../services/wardrobe.service';
import { UserModel } from 'src/app/models/user.model';
import { ProductCategoryModel } from 'src/app/models/wardrobe.model';
import { PaginatedResponse } from 'src/app/models/paginated-response.model';
import { NotificationService } from 'src/app/services/notification.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { NotificationsCountModel } from 'src/app/models/notifications.model';
import {
  ProductListingParamsModel,
  ProductModel,
} from './../../models/wardrobe.model';
import { HamburgerMenuComponent } from 'src/app/components/hamburger-menu/hamburger-menu.component';
import {
  Platform,
  LoadingController,
  ToastController,
  IonInfiniteScroll,
  ModalController,
  NavController,
} from '@ionic/angular';
import { ProductFilterModalComponent } from './../../components/product-filter-modal/product-filter-modal.component';

const CATEGORY_ICONS: { [id: number]: string } = {
  35: 'assets/icons/categories/t-shirt.svg',
  37: 'assets/icons/categories/020-blouse.svg',
  55: 'assets/icons/categories/003-sweaters_truien.svg',
  39: 'assets/icons/categories/061-jas.svg',
  36: 'assets/icons/categories/044-blazers.svg',
  42: 'assets/icons/categories/056-rokken.svg',
  41: 'assets/icons/categories/053-jurk.svg',
  47: 'assets/icons/categories/028-sneakers.svg',
  49: 'assets/icons/categories/069-hakken.svg',
  46: 'assets/icons/categories/097-laarzen.svg',
  43: 'assets/icons/categories/001-handbag.svg',
  68: 'assets/icons/categories/027-trouser.svg',
  69: 'assets/icons/categories/010-overige.svg',
};

@Component({
  selector: 'shc-home',
  templateUrl: './home.page.html',
  styleUrls: ['./home.page.scss'],
})
export class HomePage extends BasePage implements OnInit {
  loading: boolean;
  loadingMoreData = false;
  isFiltered: boolean;
  filterSavedState: any;
  segmentCurrent: string;
  searchQuery: string;
  callInProgress: boolean;
  notificationCount: number;
  products: PaginatedResponse<ProductModel>;
  categories: PaginatedResponse<ProductCategoryModel>;
  @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;
  params: ProductListingParamsModel = new ProductListingParamsModel();
  banner: any;
  userDetail: UserModel;
  @ViewChild('native-ad') nativeAd: ElementRef;

  constructor(
    protected platform: Platform,
    protected userService: UserService,
    protected authService: AuthService,
    protected translate: TranslateService,
    private errorService: ErrorHandlerService,
    protected loadingCtrl: LoadingController,
    toastController: ToastController,
    private modalCtrl: ModalController,
    private notificationService: NotificationService,
    private wardrobeService: WardrobeService,
    private router: Router,
    private localStorage: Storage,
    private route: ActivatedRoute,
  ) {
    super(
      platform,
      userService,
      authService,
      translate,
      loadingCtrl,
      toastController,
    );
  }

  ngOnInit() {
    this.route.params.subscribe((res) => {
      this.homeInit();
    });
  }

  homeInit() {
    if (!this.segmentCurrent) {
      this.segmentCurrent = 'all';
    }
    this.callInProgress = true;
    this.getCategories();
    this.authService.loggedIn.subscribe((loggedIn) => {
      if (loggedIn) {
        this.getNotificationCount();
        this.getUserDetail();
      }
    });
    this.params = new ProductListingParamsModel();
    this.localStorage.get('home-filters').then((res) => {
      if (res) {
        this.filterSavedState = res;
        this.setFiltersParam(res);
      } else {
        this.setFiltersParam();
      }
    });
  }

  segmentChanged(ev: any): void {
    if (this.callInProgress) {
      return;
    }
    this.enableScrolll();
    this.params.page = 1;
    this.segmentCurrent = ev.target.value;
    this.setOrderBy(this.segmentCurrent);
    this.getProducts('');
  }

  setOrderBy(tab: string) {
    this.enableScrolll();
    if (tab === 'recent') {
      this.params.sort = 'created_at';
      this.params.direction = 'DESC';
    } else if (tab === 'popular') {
      this.params.sort = 'views';
      this.params.direction = 'DESC';
    } else if (tab === 'highest') {
      this.params.sort = 'price';
      this.params.direction = 'DESC';
    } else if (tab === 'lowest') {
      this.params.sort = 'price';
      this.params.direction = 'ASC';
    } else {
      this.params.sort = null;
      this.params.direction = null;
    }
  }

  getProducts(event) {
    // check if filters are applying or not
    this.params = this.clean(this.params);
    console.log(this.params);
    this.isFiltered = this.isFilterApplied(this.params);
    this.loading = true;
    if (!this.loadingMoreData) {
      this.showLoader();
    }
    this.wardrobeService
      .productsList(this.params)
      .pipe(
        finalize(() => {
          if (!this.loadingMoreData) {
            this.hideLoader();
          } else {
            this.loadingMoreData = false;
          }
        }),
      )
      .subscribe(
        (res) => {
          this.loading = false;
          this.callInProgress = false;
          if (this.products && this.products.meta) {
            if (this.params.page === 1) {
              this.products = res;
            } else {
              this.products.meta = res.meta;
              this.products.data = [...this.products.data, ...res.data];
            }
          } else {
            this.products = res;
          }
          if (event) {
            event.target.complete();
          }
          this.params.page++;
        },
        (error) => {
          this.loading = false;
          this.callInProgress = false;
          this.errorService.handle(error);
        },
      );
  }

  getCategories() {
    this.wardrobeService
      .mainCategoriesList({ page: 1, per_page: 20 })
      .subscribe(
        (res) => {
          const subCategoriesOnly = res.data.filter((category) => !!category.parent_id);
          res.data = subCategoriesOnly.map((category) => ({
            ...category,
            icon: CATEGORY_ICONS[category.id] || null,
          }));
          this.categories = res;
        },
        (error) => {
          this.errorService.handle(error);
        },
      );
  }

  getUserDetail(): void {
    this.userService.getUpdatedUser().subscribe(
      (res: UserModel) => {
        this.userDetail = res;
      },
      (error) => {
        this.errorService.handle(error);
      },
    );
  }

  search(query: string): void {
    if (!query) {
      return;
    }
    this.localStorage.get('recent-searches').then((res) => {
      let recentSearches: Array<string> = res ? JSON.parse(res) : [];
      if (
        !recentSearches.includes(query) ||
        recentSearches.indexOf(query) !== 0
      ) {
        recentSearches.unshift(query);
      }
      this.localStorage.set('recent-searches', JSON.stringify(recentSearches));
      this.router.navigate(['search-result'], { state: { query } });
    });
  }

  getNotificationCount(): void {
    this.notificationService.getNotificationCounter().subscribe(
      (res: NotificationsCountModel) => {
        this.notificationCount = res.notifications_count;
      },
      (error) => {
        this.errorService.handle(error);
      },
    );
  }

  async openFilters() {
    const modal = await this.modalCtrl.create({
      component: ProductFilterModalComponent,
      componentProps: {
        type: 'home',
        data: this.filterSavedState,
      },
      cssClass: 'product-filter',
      canDismiss: true,
      id: 'productFilters',
    });
    modal.onDidDismiss().then((data) => {
      if (data && data.data) {
        this.filterSavedState = data.data;
        this.setFiltersParam(data.data);
      }
      this.enableScrolll();
      this.modalCtrl.dismiss();
    });
    return await modal.present();
  }

  setFiltersParam(filters?: any) {
    this.params.page = 1;
    this.params.size_id = filters?.size?.id;
    this.params.type_id = filters?.type?.id;
    this.params.color_id = filters?.color?.id;
    this.params.brand_id = filters?.brand?.id;
    this.params.price_to = filters?.price?.upper;
    this.params.condition_id = filters?.condition;
    this.params.price_from = filters?.price?.lower;
    this.params.category_id = filters?.category?.id;
    this.params.latitude = filters?.latitude;
    this.params.longitude = filters?.longitude;
    this.params.location = filters?.location;
    this.setOrderBy(this.segmentCurrent);
    this.getProducts('');
  }

  async openMenu() {
    const modal = await this.modalCtrl.create({
      component: HamburgerMenuComponent,
      cssClass: 'hamBurgermenu',
      id: 'hamBurgermenu',
      backdropDismiss: true,
      canDismiss: true,
    });
    modal.onDidDismiss().then((data) => {
      this.modalCtrl.dismiss();
    });
    return await modal.present();
  }

  routeToCategory(id: number) {
    this.router.navigate(['categories', 'detail', id]);
  }

  loadData(event): void {
    if (this.products.meta.current_page < this.products.meta.last_page) {
      this.loadingMoreData = true;
      this.getProducts(event);
    } else {
      this.infiniteScroll.disabled = true;
    }
  }

  enableScrolll() {
    if (this.infiniteScroll) {
      this.infiniteScroll.disabled = false;
    }
  }
}
